import { useState, useCallback, useEffect } from "react";
import { getSupplierEvaluations } from "../api/scoring-api";
import type { MemberFinalStatus } from "../api/scoring-api";
import type { SupplierEvaluationSummary } from "../types/scoring.type";

interface UseInitialEvaluationOptions {
	rfxId: number | undefined;
}

interface UseInitialEvaluationResult {
	suppliers: SupplierEvaluationSummary[];
	isLoading: boolean;
	error: string | null;
	allSuppliersScored: boolean;
	readOnly: boolean;
	notPermitted: boolean;
	memberFinalStatus: MemberFinalStatus;
	memberFinalSubmittedAt: string | null;
	refreshSupplier: (inviteId: number) => void;
}

const sortSuppliers = (suppliers: SupplierEvaluationSummary[]): SupplierEvaluationSummary[] => {
	return suppliers.sort((a,b) => a.supplierName.localeCompare(b.supplierName));
}

export const useInitialEvaluation = ({
	rfxId,
}: UseInitialEvaluationOptions): UseInitialEvaluationResult => {
	const [suppliers, setSuppliers] = useState<SupplierEvaluationSummary[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [readOnly, setReadOnly] = useState(false);
	const [notPermitted, setNotPermitted] = useState(false);
	const [memberFinalStatus, setMemberFinalStatus] = useState<MemberFinalStatus>("pending");
	const [memberFinalSubmittedAt, setMemberFinalSubmittedAt] = useState<string | null>(null);

	const allSuppliersScored =
		suppliers.length > 0 && suppliers.every((s) => s.status === "done");

	const fetchSuppliers = useCallback(async () => {
		if (!rfxId) {
			return;
		}
		setIsLoading(true);
		setError(null);
		try {
			const data = await getSupplierEvaluations(rfxId);
			setSuppliers(sortSuppliers(data.suppliers));
			setReadOnly(data.readOnly);
			setNotPermitted(data.notPermitted);
			setMemberFinalStatus(data.memberFinalStatus);
			setMemberFinalSubmittedAt(data.memberFinalSubmittedAt);
		} catch {
			setError("Failed to load supplier evaluations");
		} finally {
			setIsLoading(false);
		}
	}, [rfxId]);

	useEffect(() => {
		fetchSuppliers();
	}, [fetchSuppliers]);

	const refreshSupplier = useCallback(
		(inviteId: number) => {
			if (!rfxId) {
				return;
			}
			getSupplierEvaluations(rfxId)
				.then((data) => {
					setSuppliers(sortSuppliers(data.suppliers));
					setReadOnly(data.readOnly);
					setMemberFinalStatus(data.memberFinalStatus);
					setMemberFinalSubmittedAt(data.memberFinalSubmittedAt);
				})
				.catch(() => {
					// Silent refresh failure — table still shows last known state
				});
		},
		[rfxId]
	);

	return {
		suppliers,
		isLoading,
		error,
		allSuppliersScored,
		readOnly,
		notPermitted,
		memberFinalStatus,
		memberFinalSubmittedAt,
		refreshSupplier,
	};
};
